import React, { useEffect, useMemo, useState } from "react";
import { PictureOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { useRealtimeRun } from "@trigger.dev/react-hooks";
import { Button, Checkbox, message, Popconfirm, Typography } from "antd";

import { DISH_IMAGE_MODEL, estimateImageGenerationCostUsd } from "@fitness-app/ai/pricing";
import { mealsPlanActions, traineeMealsPlanActions } from "@fitness-app/app-store";
import { collectDishesToIllustrate } from "@fitness-app/utils/src/nutrition/dishImages";

import { PermissionKey } from "@fitness-app/data-models";

import { usePermission } from "~/hooks/usePermission";
import { usePostHogFeatureFlag } from "~/hooks/usePostHogFeatureFlag";
import { useBackgroundTasksQuery } from "~/modules/Dashboard/BackgroundTasksDrawer/useBackgroundTasks";
import { MEALS_PLAN_DISH_IMAGES_FEATURE_FLAG } from "~/modules/Nutrition/dishImagesFeatureFlag";
import { useMealsPlanContext } from "~/shared/providers/MealsPlanProvider";
import { supabase, useAppDispatch } from "~/store/initializeStore";

const FINISHED_STATUSES = new Set(["COMPLETED", "FAILED", "CANCELED", "TIMED_OUT", "CRASHED", "SYSTEM_FAILURE"]);

type DishImagesSummary = { generated?: number; failed?: number; costUsd?: number };

const formatCost = (costUsd: number | null) => (costUsd === null ? "?" : `$${costUsd.toFixed(2)}`);

/** `template` = szablon diety w bibliotece, `client` = dieta przypisana konkretnej klientce. */
export type DishImagesPlanType = "template" | "client";

interface GenerateMissingDishImagesProps {
  /** `meals_plan.id` dla szablonu, `client_meals_plan.id` dla diety klientki. */
  planId: string;
  planType: DishImagesPlanType;
}

/**
 * Masowe dogenerowanie zdjęć potrawom w planie żywieniowym (TRE-1830).
 *
 * Liczbę zdjęć i koszt liczymy z tego samego `collectDishesToIllustrate`, po którym pojedzie task,
 * więc to, co trenerka zatwierdza w Popconfirmie, jest tym, za co zapłaci. Po zakończeniu runa
 * przeładowujemy plan — zdjęcia dopisuje task prosto do bazy, więc bez tego panel pokazywałby
 * dalej stare, puste kafelki.
 */
export const GenerateMissingDishImages = ({ planId, planType }: GenerateMissingDishImagesProps) => {
  const dispatch = useAppDispatch();
  const { planDetails } = useMealsPlanContext();
  const canGenerateDishImages =
    usePermission(PermissionKey.NUTRITION_MANAGE) && usePermission(PermissionKey.AGENTS_USE);
  const { enabled: isFeatureEnabled } = usePostHogFeatureFlag(MEALS_PLAN_DISH_IMAGES_FEATURE_FLAG);
  const { createMealsPlanDishImages, isCreatingMealsPlanDishImages } = useBackgroundTasksQuery({ enabled: false });
  const [activeRun, setActiveRun] = useState<{ id: string; accessToken: string; requestId: string } | null>(null);
  const [overwrite, setOverwrite] = useState(false);

  const { missingCount, allCount } = useMemo(
    () => ({
      missingCount: collectDishesToIllustrate(planDetails?.weeks).size,
      allCount: collectDishesToIllustrate(planDetails?.weeks, { overwrite: true }).size,
    }),
    [planDetails?.weeks],
  );

  const withPhotoCount = allCount - missingCount;
  const selectedCount = overwrite ? allCount : missingCount;

  const estimatedCost = useMemo(
    () => estimateImageGenerationCostUsd({ model: DISH_IMAGE_MODEL, images: selectedCount }),
    [selectedCount],
  );

  const { run } = useRealtimeRun(activeRun?.id, {
    enabled: Boolean(activeRun),
    accessToken: activeRun?.accessToken,
  });

  /**
   * Drugie, niezależne źródło informacji o zakończeniu.
   *
   * Zdjęcia dopisuje task prosto do bazy, więc bez przeładowania planu trenerka patrzy na stare,
   * puste kafelki. Poleganie wyłącznie na strumieniu z trigger.dev okazało się zawodne — gdy SSE
   * nie dowiezie ostatniego statusu, run kończy się po cichu i widok zostaje nieodświeżony.
   * Wiersz `ai_agent_request` jest zapisywany przez sam task, więc odpytanie go co kilka sekund
   * daje pewny sygnał niezależny od tego, czy połączenie realtime przeżyło.
   */
  const requestStatusQuery = useQuery(["dishImagesRequestStatus", activeRun?.requestId], {
    enabled: Boolean(activeRun?.requestId),
    refetchInterval: 4000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ai_agent_request")
        .select("status, data")
        .eq("id", activeRun!.requestId)
        .maybeSingle();

      if (error) throw new Error(error.message);
      return data as { status: string; data: { dishImages?: DishImagesSummary } | null } | null;
    },
  });

  const runStatus = run?.status;
  const requestStatus = requestStatusQuery.data?.status;
  const isSuccess = runStatus === "COMPLETED" || requestStatus === "COMPLETED";
  const isFinished =
    isSuccess ||
    Boolean(runStatus && FINISHED_STATUSES.has(runStatus)) ||
    requestStatus === "FAILED" ||
    requestStatus === "CANCELLED";

  useEffect(() => {
    if (!activeRun || !isFinished) {
      return;
    }

    setActiveRun(null);
    setOverwrite(false);

    if (!isSuccess) {
      void message.error("Generowanie zdjęć nie powiodło się — szczegóły w zadaniach w tle.");
      return;
    }

    // Osobne dispatche zamiast ternary w argumencie: to dwa różne thunki i ich unia nie przechodzi
    // przez typ `dispatch`.
    if (planType === "client") {
      void dispatch(traineeMealsPlanActions.fetchMealsPlanWithDetails({ id: planId }));
    } else {
      void dispatch(mealsPlanActions.fetchMealsPlanWithDetails({ id: planId }));
    }

    const summary = (requestStatusQuery.data?.data?.dishImages ?? run?.output) as DishImagesSummary | undefined;
    void message.success(
      `Wygenerowano ${summary?.generated ?? 0} zdjęć${summary?.failed ? `, nie udało się ${summary.failed}` : ""} (koszt ${formatCost(summary?.costUsd ?? null)}).`,
    );
  }, [activeRun, isFinished, isSuccess, requestStatusQuery.data, run?.output, dispatch, planId, planType]);

  // Ten sam próg co backend (authorizeNutritionAgentAccess): nutrition.manage +
  // agents.use w dowolnym scope — katalog dań jest wspólny dla całego staffu.
  if (!canGenerateDishImages) {
    return null;
  }

  if (!isFeatureEnabled) {
    return null;
  }

  // Pusty plan nie ma czego generować — ale przy komplecie zdjęć przycisk zostaje, bo z
  // zaznaczonym „nadpisz" da się je wygenerować od nowa.
  if (!planId || (!allCount && !activeRun)) {
    return null;
  }

  const isRunning = Boolean(activeRun);

  return (
    <Popconfirm
      title="Wygenerować zdjęcia potraw?"
      okText="Generuj"
      cancelText="Anuluj"
      disabled={isRunning}
      okButtonProps={{ disabled: !selectedCount }}
      onOpenChange={(open) => {
        if (!open) {
          setOverwrite(false);
        }
      }}
      description={
        <div className="max-w-xs">
          <Typography.Paragraph className="!mb-1">
            Zdjęć do wygenerowania: <strong>{selectedCount}</strong> (powtórzenia tej samej potrawy liczone raz).
          </Typography.Paragraph>
          {/* Checkbox pokazujemy tylko wtedy, gdy jest co nadpisywać — inaczej byłby to martwy
              przełącznik, który niczego nie zmienia w liczbie zdjęć. */}
          {withPhotoCount > 0 && (
            <Checkbox checked={overwrite} className="!mb-2" onChange={(event) => setOverwrite(event.target.checked)}>
              Nadpisz {withPhotoCount} {withPhotoCount === 1 ? "istniejące zdjęcie" : "istniejących zdjęć"}
            </Checkbox>
          )}
          <Typography.Paragraph className="!mb-0">
            <Typography.Text type={overwrite ? "warning" : "secondary"}>
              Szacowany koszt: <strong>{formatCost(estimatedCost)}</strong>.{" "}
              {overwrite
                ? "Nadpisujesz także potrawy, które mają już zdjęcie — płacisz za nie ponownie."
                : "Generowanie leci w tle, postęp widać w zadaniach w tle."}
            </Typography.Text>
          </Typography.Paragraph>
        </div>
      }
      onConfirm={async () => {
        try {
          const response = await createMealsPlanDishImages({
            planId,
            planType,
            expectedDishes: selectedCount,
            ...(overwrite ? { overwrite: true } : {}),
          });
          setActiveRun({ id: response.run.id, accessToken: response.accessToken, requestId: response.requestId });
        } catch {
          // `mutateAsync` odrzuca promise, a komunikat pokazuje już `onError` w
          // `useBackgroundTasksQuery`. Bez tego złapania leci unhandled rejection.
        }
      }}
    >
      <Button size="small" icon={<PictureOutlined />} loading={isCreatingMealsPlanDishImages || isRunning}>
        {isRunning ? "Generowanie zdjęć..." : missingCount ? `Wygeneruj zdjęcia (${missingCount})` : "Zdjęcia potraw"}
      </Button>
    </Popconfirm>
  );
};
